/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import omit from 'lodash/omit';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// uncomment to use mock functions for testing
// import { mock, mockThrow } from './shared/mocks/core-api.mocks';
import { MalwareFinding, TriageDecision } from './malware-triage/malware-triage.types';
import { AuditTrailReportPost } from './projects/project-audit-reports/create-audit-trail-dialog/create-audit-trail-dialog.component';
import { ProjectPermissions } from './projects/projects.service';
import { ProjectReportOptions, ProjectReportsResponse } from './reporting/project-report/project.types';
import {
  UsageDashboard,
  UsageReportCsvOptions,
  UsageReportLineItem,
  UsageReportOptions,
} from './reporting/usage-report/usage.types';
import { downloadFile } from './shared/browser.utils';
import { pluralize } from './shared/container.utils';
import { ContainerType } from './shared/enums/container-type.enum';
import { JobPriority } from './shared/enums/job-priority.enum';
import { PermissionAction } from './shared/enums/permission-action.enum';
import { TaskFacet } from './shared/enums/task.enum';
import { AccessLog } from './shared/models/access-log.model';
import { Acquisition } from './shared/models/acquisition.model';
import { AddAnalysis, Analysis } from './shared/models/analysis.model';
import {
  Annotation,
  Overlay,
  OverlayCountRequest,
  OverlayCountResult,
  Segmentation,
} from './shared/models/annotations.types';
import { ApiConfig, DeleteReasons, ProjectLockingReasons } from './shared/models/api-config.model';
import { ApiKeyParams, ApiKeyResult } from './shared/models/api-key.model';
import { ApiOperation } from './shared/models/api-operation.model';
import { AuditTrail } from './shared/models/audit-trail.model';
import { BatchJob, ClassicBatchProposalInput } from './shared/models/batch.model';
import { BulkMoveSessions, BulkMoveSessionsConflict } from './shared/models/bulk.model';
import { ChangeLog } from './shared/models/change-log.model';
import { Collection } from './shared/models/collection.model';
import { ContainerInfo } from './shared/models/container.model';
import { CustomForm, CustomFormInput, CustomFormModify } from './shared/models/custom-form.model';
import { CvatExportFormats } from './shared/models/cvat.model';
import {
  DataView,
  DataViewExecution,
  DataViewPayload,
  DataViewResponse,
  DataViewsCreateResponse,
  ViewColumn,
} from './shared/models/data-view.model';
import {
  BulkTaskDryrunResult as DeprecatedBulkTaskDryrunResult,
  BulkTaskInput as DeprecatedBulkTaskInput,
  BulkTasksDeletePayload as DeprecatedBulkTasksDeletePayload,
  DryrunTaskDeletionPreview as DeprecatedDryrunTaskDeletionPreview,
  MatchingContainerCount as DeprecatedMatchingContainerCount,
  Task as DeprecatedTask,
  TaskDetails as DeprecatedTaskDetails,
  TaskInput as DeprecatedTaskInput,
  TaskParentsEstimateFilter as DeprecatedTaskParentsEstimateFilter,
} from './shared/models/deprecated-task.model';
import { Device, DeviceUpdate } from './shared/models/device.model';
import { DeidProfile, DicomPeer } from './shared/models/dicom.model';
import { DimsePeersTest, DimseProject } from './shared/models/dimse.model';
import { DownloadParams, DownloadRequest, DownloadSummary, DownloadTicket } from './shared/models/download.model';
import {
  ArchiveMemberInfo,
  File,
  FileClassificationChange,
  FileCounts,
  FileInfo,
  FileMoveBody,
  FileTypes,
  FileVersion,
} from './shared/models/file.model';
import { Form, FormInput } from './shared/models/form.model';
import { FormResponseBase, FormResponseCreate, FormResponseOutput } from './shared/models/form-response.model';
import { Gear, GearPermissions, GearSeries } from './shared/models/gear.model';
import { GearRule, GearRuleInput } from './shared/models/gear-rule.model';
import { GearSuggestions } from './shared/models/gear-suggestions.model';
import { Group, GroupSettings } from './shared/models/group.model';
import {
  AddJob,
  Job,
  JobBulkActionResult,
  JobContextInputs,
  JobDetail,
  JobLogResponse,
  JobPriorityUpdateActionResult,
} from './shared/models/job.model';
import { Modality } from './shared/models/modality.model';
import { Note } from './shared/models/note.model';
import { Paginated } from './shared/models/paginated.model';
import {
  AccessPermission,
  PermissionCheckInput,
  PermissionCheckOutput,
  RolePermission,
} from './shared/models/permission.model';
import { MultifactorPrompt, MultifactorSettings, Profile } from './shared/models/profile.model';
import {
  CatalogProject,
  ListProject,
  Project,
  ProjectCopyInput,
  ProjectCopyOutput,
  ProjectCopyStatus,
  ProjectLabel,
  ProjectSettings,
  ProjectSharingFilterOptions,
} from './shared/models/project.model';
import { ProjectTemplate } from './shared/models/project-template.model';
import { Protocol, ProtocolInput, ProtocolModify } from './shared/models/protocol.model';
import { Provider } from './shared/models/provider.model';
import { ReadTaskProtocol, ReadTaskProtocolInput } from './shared/models/read-task-protocol.model';
import { Resolve } from './shared/models/resolve.model';
import { Role } from './shared/models/role.model';
import { QueryValidation } from './shared/models/search/query-validation.model';
import { SavedSearch } from './shared/models/search/saved-search.model';
import { SearchFacets } from './shared/models/search/search-facets.model';
import { SearchField } from './shared/models/search/search-field.model';
import { SearchFieldAggregation } from './shared/models/search/search-field-aggregation.model';
import { SearchFilter, SearchQuery } from './shared/models/search/search-query.model';
import { SearchResultResponse } from './shared/models/search/search-results.model';
import { SearchStatus } from './shared/models/search/search-status.model';
import { SearchSuggestionResponse } from './shared/models/search/search-suggestions.model';
import { Session, SessionPostPayload } from './shared/models/session.model';
import { SiteBookmark, SiteSettings } from './shared/models/site-settings.model';
import { Snapshot } from './shared/models/snapshot.model';
import { StaffingPool, StaffingPoolPayload, UserStaffingPoolsPayload } from './shared/models/staffing-pool.model';
import { Subject } from './shared/models/subject.model';
import { BulkTagsPayload } from './shared/models/tags.model';
import {
  AdjudicationTaskCreate,
  BatchAdjudicationTaskCreate,
  BatchReaderTaskCreate,
  ReaderTask,
  ReaderTaskCreate,
  ReaderTaskModify,
  TaskAssign,
  TaskReport,
  UploadTask,
} from './shared/models/task.model';
import { TreeQuery, TreeResult } from './shared/models/tree-query.model';
import { User, UserPreferences } from './shared/models/user.model';
import { Version } from './shared/models/version.model';
import { ViewerConfig, ViewerConfigInput } from './shared/models/viewer-config.model';
import { JupyterLabServer, JupyterLabServerInput, JupyterLabServerOption } from './shared/models/workspace.model';
import {
  Export,
  ExportProgress2,
  StorageCheckResponse,
  XferBlob,
  XferBlobCreate,
  XferBlobFinish,
  XferBlobSession,
  XferBlobSessionCreate,
  XferBlobUploadResponse,
  XferConflict,
  XferConflictResolveInput,
  XferConflictTypeResolveMethods,
  XferConnectorResponse,
  XferExport,
  XferImport,
  XferImportCreate,
  XferImportProgress,
  XferPendingBlob,
  XferRule,
  XferRuleCreate,
  XferRuleSchema,
  XferRuleSets,
  XferSchedule,
  XferStorage,
  XferStoreSCPS,
  XferStoreSCPsPemCertificate,
  XferStoreSCU,
  XferUrl,
  XferVersion,
} from './shared/models/xfer.model';

type ParamValue = string | number | boolean;
export interface ApiParams {
  [param: string]: ParamValue | ParamValue[];
  /**
   * core-api filter format is comma-delimited e.g.
   *   filter=project._id=1234,origin.id=jz@fw.io
   *   Pass a pre-joined string in this case.
   * Otherwise passing an array of filters will repeat the param e.g.
   *   filter=project._id=1234&filter=origin.id=jz@fw.io
   *   This is the default list format for FastAPI.
   */
  filter?: string | string[];
  sort?: string;
  limit?: number;
  skip?: number;
  page?: number;
  after_id?: string;
  /**
   * Comma-separated list of columns to include in response.
   * Currently supported by reader tasks endpoint.
   */
  columns?: string;
}

export interface ApiHeaders {
  [name: string]: string | string[];
}

export interface WriteResult {
  deleted?: number;
  modified?: number;
}

export interface DeleteResult {
  deleted?: number;
  not_found?: number;
}

/** Endpoints shared by the v3 overlay resources (annotations, segmentations) */
export interface OverlayEndpoints<T extends Overlay> {
  get: (params: ApiParams) => Observable<T[]>;
  count: (countRequest: OverlayCountRequest) => Observable<OverlayCountResult>;
  getFile: (id: string, params?: ApiParams, headers?: ApiHeaders) => Observable<Blob>;
  delete: (id: string, params?: ApiParams) => Observable<DeleteResult>;
  deleteVersion: (id: string, version: number, params?: ApiParams) => Observable<DeleteResult>;
  restoreVersion: (id: string, version: number) => Observable<T>;
  getDeleteReasons: () => Observable<DeleteReasons>;
}

export interface BulkWriteResult {
  success: string[];
  error: string[];
}

enum CoreFeature {
  ExcludeFiles = 'exclude-files',
  Pagination = 'pagination',
  SlimContainers = 'slim-containers',
}
const featureHeader = (...features: CoreFeature[]): ApiHeaders => ({
  'X-Accept-Feature': features.join(','),
});

/**
 * Service for interacting with and typing Core API calls
 */
@Injectable({
  providedIn: 'root',
})
export class CoreApiService {
  private http = inject(HttpClient);

  readonly acquisitions = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Acquisition[]>>('/api/acquisitions', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
    get: (acquisitionId: string, params: ApiParams = {}) =>
      this.get<Acquisition>(`/api/acquisitions/${acquisitionId}`, params, {
        headers: createHeaders(featureHeader(CoreFeature.SlimContainers)),
      }),
    put: (acquisitionId: string, body: Partial<Acquisition>) =>
      this.put<WriteResult>(`/api/acquisitions/${acquisitionId}`, body),
    post: (body: Partial<Acquisition>) => this.post<Partial<Acquisition>>('/api/acquisitions', body),
    delete: (acquisitionId: string, params?: ApiParams) =>
      this.delete<WriteResult>(`/api/acquisitions/${acquisitionId}`, { params }),
    deleteBulk: (acquisitionIds: string[], params?: ApiParams) =>
      this.delete<WriteResult>('/api/acquisitions', { params, body: acquisitionIds }),
    deleteQuery: (query: SearchFilter[], params?: ApiParams) =>
      this.delete<WriteResult>('/api/acquisitions/delete_by_query', { params, body: query }),
  };

  readonly analyses = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Analysis[]>>('/api/analyses', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (analysisId: string, params: ApiParams = {}) => this.get<Analysis>(`/api/analyses/${analysisId}`, params),
    put: (analysisId: string, body: Partial<Analysis>) => this.put<WriteResult>(`/api/analyses/${analysisId}`, body),
    post: (parentType: ContainerType, parentId: string, body: AddAnalysis) =>
      this.post<Pick<Analysis, '_id'>>(`/api/${pluralize(parentType)}/${parentId}/analyses`, body),
    delete: (containerType: string, containerId: string, analysisId: string, params?: ApiParams) =>
      this.delete<null>(`/api/${containerType}/${containerId}/analyses/${analysisId}`, { params }),
  };

  readonly bulk = {
    move: {
      acquisitions: (body: Partial<BulkMoveSessions>) =>
        this.post<BulkMoveSessionsConflict[]>('/api/bulk/move/acquisitions', body),
      sessions: (body: Partial<BulkMoveSessions>) =>
        this.post<BulkMoveSessionsConflict[]>('/api/bulk/move/sessions', body),
    },
    tags: {
      post: (body: BulkTagsPayload) => this.post<BulkWriteResult>('/api/bulk/add/tags', body),
      delete: (body: BulkTagsPayload) => this.post<BulkWriteResult>('/api/bulk/remove/tags', body),
    },
    tasks: {
      delete: (body: DeprecatedBulkTasksDeletePayload) =>
        this.post<BulkWriteResult>('/api/bulk/delete/readertasks', body),
    },
  };

  readonly changeLog = {
    get: (containerType: string, containerId: string, params?: ApiParams) =>
      this.get<ChangeLog>(`/api/changes/${containerType}/${containerId}`, params),
    field: (containerType: string, containerId: string, field: string, params?: ApiParams) =>
      this.get<ChangeLog>(`/api/changes/${containerType}/${containerId}/fields/${field}`, params),
  };

  readonly collections = {
    query: (params?: ApiParams) => this.get<Collection[]>('/api/collections', params),
    get: (collectionId: string, params?: ApiParams) => this.get<Collection>(`/api/collections/${collectionId}`, params),
    post: (body: Partial<Collection>) => this.post<Pick<Collection, '_id'>>('/api/collections', body),
    put: (collectionId: string, body: { contents: ApiOperation }) =>
      this.put<Collection>(`/api/collections/${collectionId}`, body),
    delete: (collectionId: string) => this.delete<null>(`/api/collections/${collectionId}`),
    sessions: (collectionId: string, params: ApiParams) =>
      this.get<Paginated<Session[]>>(`/api/collections/${collectionId}/sessions`, params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    removeSessions: (collectionId: string, body: ApiOperation) =>
      this.put<Session>(`/api/collections/${collectionId}`, body),
    updateDesription: {
      put: (collectionId: string, description: string) =>
        this.put<{ modified: number; jobs_spawned: number }>(`/api/collections/${collectionId}`, { description }),
    },
    permissions: {
      post: (collectionId: string, body: { _id: string; access: string }) =>
        this.post<null>(`/api/collections/${collectionId}/permissions`, body),
      put: (collectionId: string, body: { _id: string; access: string }) =>
        this.put<null>(`/api/collections/${collectionId}/permissions/${body._id}`, body),
      delete: (collectionId: string, userId: string) =>
        this.delete<null>(`/api/collections/${collectionId}/permissions/${userId}`),
    },
  };

  readonly config = {
    get: () => this.get<ApiConfig>('/api/config'),
    deleteReasons: {
      get: () => this.get<DeleteReasons>('/api/config/delete_reasons'),
    },
    fileTypes: {
      get: () => this.get<FileTypes>('/api/config/file_types'),
    },
    projectLockingReasons: {
      get: () => this.get<ProjectLockingReasons>('/api/config/project_locking_reasons'),
    },
    projectSharingFilterOptions: {
      get: () => this.get<ProjectSharingFilterOptions>('/api/config/project-sharing-options'),
    },
  };

  readonly containers = {
    query: <Type>(containerType: string, params: ApiParams = {}) =>
      this.get<Paginated<Type[]>>(`/api/${containerType}`, params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
    get: <Type>(containerId: string, params: ApiParams = {}) =>
      this.get<Type>(`/api/containers/${containerId}`, params, {
        headers: createHeaders(featureHeader(CoreFeature.SlimContainers)),
      }),
    put: <Type>(containerId: string, body: Partial<Type>) =>
      this.put<WriteResult>(`/api/containers/${containerId}`, body),
    delete: <Type>(containerId: string, params?: ApiParams) =>
      this.delete<Type>(`/api/containers/${containerId}`, { params }),
    analyses: {
      query: (containerType: string, containerId: string, params: ApiParams = {}) =>
        this.get<Paginated<Analysis[]>>(`/api/${containerType}/${containerId}/analyses`, params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }),
      post: (containerId: string, body: AddAnalysis, params?: ApiParams) =>
        this.post<Pick<Analysis, '_id'>>(`/api/containers/${containerId}/analyses`, body, params),
    },
    files: {
      delete: (containerId: string, fileName: string, params?: ApiParams) =>
        this.delete<WriteResult>(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}`, { params }),
      info: {
        get: (containerId: string, fileName: string, params: ApiParams = {}) =>
          this.get<File>(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}/info`, params),
        patch: (containerId: string, fileName: string, body) =>
          this.patch<null>(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}/info`, body),
      },
      /**
       * Get a ticket for a file that can be used in query parameters in place of the Authorization header.
       * @deprecated use flywheel.files.ticket
       */
      ticket: (containerId: string, fileName: string, params: ApiParams = {}) =>
        this.get<DownloadTicket>(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}`, {
          ...params,
          ticket: '',
        }),
      /**
       * Get file contents as a string.
       * @deprecated use flywheel.files.text
       */
      text: (containerId: string, fileName: string, params: ApiParams = {}) =>
        this.get<string>(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}`, params, {
          responseType: 'text',
        }),
      /**
       * Get file contents as a Blob.
       * @deprecated use flywheel.files.blob
       */
      blob: (containerId: string, fileName: string, params: ApiParams = {}, headers?: ApiHeaders) =>
        this.get<Blob>(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}`, params, {
          headers,
          responseType: 'blob',
        }),
      classifications: {
        patch: (containerId: string, fileName: string, body) =>
          this.patch<{ modified: number; jobs_spawned: number }>(
            `/api/containers/${containerId}/files/${encodeURIComponent(fileName)}/classification`,
            body,
          ),
      },
      members: (containerId: string, fileName: string, params: ApiParams = {}) =>
        this.get<ArchiveMemberInfo>(`/api/containers/${containerId}/files/${encodeURIComponent(fileName)}`, {
          ...params,
          info: true,
        }),
      /**
       * Upload files and report progress.
       */
      upload: (containerId: string, form: FormData, params?: { preserve_metadata: boolean }) =>
        this.post<HttpEvent<File[]>>(`/api/containers/${containerId}/files`, form, {
          observe: 'events',
          params,
          reportUploadProgress: true,
        }),
    },
    info: {
      patch: (containerId: string, body: { replace: ContainerInfo }) =>
        this.patch<null>(`/api/containers/${containerId}/info`, body),
    },
    notes: {
      post: (containerId: string, body: Partial<Note>) => this.post<null>(`/api/containers/${containerId}/notes`, body),
      delete: (containerId: string, noteId: string) =>
        this.delete<null>(`/api/containers/${containerId}/notes/${noteId}`),
    },
    tags: {
      post: (containerId: string, body: { value: string }) =>
        this.post<null>(`/api/containers/${containerId}/tags`, body),
      delete: (containerId: string, tagValue: string) =>
        this.delete<null>(`/api/containers/${containerId}/tags/${encodeURIComponent(tagValue)}`),
    },
  };

  readonly cvat = {
    fileAccess: {
      post: (fileId: string) => this.post<string>(`/api/cvat/file-access/${fileId}`),
    },
  };

  readonly dataexplorer = {
    facets: (body: Partial<SearchQuery>) => this.post<{ facets: SearchFacets }>('/api/dataexplorer/facets', body),
    fields: (body: { field: string }) => this.post<SearchField[]>('/api/dataexplorer/search/fields', body),
    fieldsAggregate: (body: { search_string: string; field_name: string }) =>
      this.post<SearchFieldAggregation>('/api/dataexplorer/search/fields/aggregate', body),
    parse: (body: Partial<SearchQuery>) => this.post<QueryValidation>('/api/dataexplorer/search/parse', body),
    search: (params: ApiParams, body: SearchQuery) =>
      this.post<SearchResultResponse>('/api/dataexplorer/search', body, { params: createParams(params) }),
    searchExport: (params: ApiParams, body: SearchQuery) =>
      this.post<any>('/api/dataexplorer/search', body, {
        params: createParams(params),
        headers: {
          accept: 'text/csv',
        },
        responseType: 'text',
      }),
    status: () => this.get<SearchStatus>('/api/dataexplorer/search/status'),
    suggest: (body: Partial<SearchQuery>) =>
      this.post<SearchSuggestionResponse>('/api/dataexplorer/search/suggest', body),
    training: (body: any) => this.post<any>('/api/dataexplorer/search/training', body),
    queries: {
      query: (params: ApiParams = {}) => this.get<SavedSearch[]>('/api/dataexplorer/queries', params),
      post: (body: Partial<SavedSearch>) => this.post<SavedSearch>('/api/dataexplorer/queries', body),
      get: (queryId: string) => this.get<SavedSearch>(`/api/dataexplorer/queries/${queryId}`),
      delete: (queryId: string) => this.delete<WriteResult>(`/api/dataexplorer/queries/${queryId}`),
    },
  };

  readonly dataViewExecutions = {
    get: (viewId: string) => this.get<DataViewExecution>(`/api/data_view_executions/${viewId}`),
    query: (params?: ApiParams) =>
      this.get<Paginated<DataViewExecution[]>>('/api/data_view_executions', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    data: {
      get: (viewId: string) => this.get<DataViewResponse>(`/api/data_view_executions/${viewId}/data`),
      columns: (viewId: string) => this.get<string[]>(`/api/data_view_executions/${viewId}/data/columns`),
      ticket: (viewId: string, format: string) =>
        this.post<DownloadTicket>(`/api/data_view_executions/${viewId}/ticket?file_format=${format}`),
    },
    delete: (viewId: string) => this.post<any>(`/api/data_view_executions/${viewId}/delete`),
    save: (viewId: string, format = 'csv') =>
      this.post<File>(`/api/data_view_executions/${viewId}/save?file_format=${format}`),
  };

  readonly devices = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Device[]>>('/api/devices', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    post: (body: Partial<Device>) => this.post<Device>('/api/devices', body),
    get: (deviceId: string) => this.get<Device>(`/api/devices/${deviceId}?join_key=true`),
    put: (deviceId: string, body: DeviceUpdate) => this.put<null>(`/api/devices/${deviceId}`, body),
    deleteKey: (deviceId: string, keyId: string) => this.delete<null>(`/api/devices/${deviceId}/key/${keyId}`),
    generateKey: (deviceId: string, params: ApiKeyParams) =>
      this.post<ApiKeyResult>(`/api/devices/${deviceId}/key`, params),
  };

  readonly dimse = {
    peers: {
      get: () => this.get<DicomPeer[]>('/api/dimse/services'),
      post: (data: Partial<DicomPeer>) => this.post<DicomPeer>('/api/dimse/services', data),
      delete: (aet: string) => this.delete<WriteResult>(`/api/dimse/services/${aet}`),
    },
    projects: {
      get: (projectId: string) => this.get<DimseProject>(`/api/dimse/projects/${projectId}`),
      list: () => this.get<DimseProject[]>('/api/dimse/projects'),
      create: (projectId: string, aet?: string) =>
        this.post<DimseProject>('/api/dimse/projects', { project_id: projectId, aet }),
      delete: (aet: string) => this.delete<WriteResult>(`/api/dimse/projects/${aet}`),
    },
  };

  readonly downloads = {
    createTicket: (body: DownloadRequest, params?: DownloadParams) =>
      this.post<DownloadTicket>('/api/download', body, { params }),
    createReadTaskTicket: (body: DownloadRequest, prefix?: string, params?: DownloadParams) =>
      this.post<DownloadTicket>(`/api/download?type=read_task&prefix=${prefix}`, body, { params }),
    downloadFile: (filename: string, ticket: string) => {
      downloadFile(filename, `/api/download?ticket=${ticket}&format=zip`);
    },
    summary: (body: DownloadRequest, params?: DownloadParams) =>
      this.post<DownloadSummary>('/api/download/summary', body, { params }),
  };

  readonly files = {
    list: (params?: ApiParams) => this.get<Paginated<File[]>>('/api/files', params),
    get: (fileId: string, params?: { version?: number }) => this.get<File>(`/api/files/${fileId}`, params),
    put: (fileId: string, body: Partial<File>) => this.put<void>(`/api/files/${fileId}`, body),
    delete: (fileId: string, params?: { version?: number; delete_reason?: string; force?: boolean }) =>
      this.delete<WriteResult>(`/api/files/${fileId}`, { params }),
    restore: (fileId: string, params: { version: number; evaluate_gear_rules: boolean }) =>
      this.post<WriteResult>(`/api/files/${fileId}/restore`, null, { params }),
    versions: (fileId: string, params?: ApiParams) => this.get<FileVersion[]>(`/api/files/${fileId}/versions`, params),
    ticket: (fileId: string, params?: ApiParams) =>
      this.post<DownloadTicket>(`/api/files/${fileId}/ticket`, null, { params }),
    text: (fileId: string, params?: ApiParams) =>
      this.get<string>(`/api/files/${fileId}/download`, { ...params, view: true }, { responseType: 'text' }),
    blob: (fileId: string, params?: ApiParams, headers?: ApiHeaders) =>
      this.get<Blob>(`/api/files/${fileId}/download`, { ...params, view: true }, { headers, responseType: 'blob' }),
    signedUrl: (fileId: string, params?: ApiParams) => this.get<string>(`/api/files/${fileId}/signed_url`, params),
    zipInfo: (fileId: string, params?: ApiParams) =>
      this.get<ArchiveMemberInfo>(`/api/files/${fileId}/zip_info`, params),
    zipMember: (fileId: string, memberPath: string, params?: ApiParams, headers?: ApiHeaders) =>
      this.get<Blob>(
        `/api/files/${fileId}/zip_member/${encodeURIComponent(memberPath)}`,
        { ...params, view: true },
        { headers, responseType: 'blob' },
      ),
    tags: {
      get: (fileId: string) => this.get<string[]>(`/api/files/${fileId}/tags`),
      put: (fileId: string, body: string[]) => this.put<WriteResult>(`/api/files/${fileId}/tags`, body),
      patch: (fileId: string, body: string[]) => this.patch<WriteResult>(`/api/files/${fileId}/tags`, body),
      delete: (fileId: string, body: string[]) => this.delete<any>(`/api/files/${fileId}/tags`, { body }),
    },
    info: {
      get: (fileId: string, params?: ApiParams) => this.get<FileInfo>(`/api/files/${fileId}/info`, params),
      put: (fileId: string, body: FileInfo) => this.put<void>(`/api/files/${fileId}/info`, body),
      patch: (fileId: string, body: FileInfo) => this.patch<void>(`/api/files/${fileId}/info`, body),
    },
    classification: (fileId: string, body: FileClassificationChange) =>
      this.put<void>(`/api/files/${fileId}/classification`, body),
    move: (fileId: string, body: FileMoveBody) =>
      this.post<{ modified: number; jobs_spawned: number }>(`/api/files/${fileId}/move`, body),
  };

  readonly forms = {
    get: (formId: string) => this.get<Form>(`/api/forms/${formId}`),
    query: (params: ApiParams = {}) => this.get<Paginated<Form[]>>('/api/forms', params),
    post: (body: FormInput) => this.post<Form>('/api/forms', body),
    put: (formId: string, body: FormInput) => this.put<Form>(`/api/forms/${formId}`, body),
    delete: (formId: string) => this.delete<WriteResult>(`/api/forms/${formId}`),
  };

  readonly gears = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Gear[]>>('/api/gears', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (gearId: string) => this.get<Gear>(`/api/gears/${gearId}`),
    permissions: {
      put: (gearName: string, body: GearPermissions) =>
        this.put<GearSeries>(`/api/gears/${gearName}/permissions`, body),
    },
    series: {
      get: (gearName: string) => this.get<GearSeries>(`/api/gears/${gearName}/series`),
      put: (gearName: string, body: GearSeries) => this.put<GearSeries>(`/api/gears/${gearName}/series`, body),
    },
    suggest: (gearId: string, containerType: string, containerId: string, params: ApiParams = {}) =>
      this.get<GearSuggestions>(`/api/gears/${gearId}/suggest/${containerType}/${containerId}`, params),
    context: (gearId: string, containerType: ContainerType, containerId: string) =>
      this.get<JobContextInputs>(`/api/gears/${gearId}/context/${pluralize(containerType)}/${containerId}`),
  };

  readonly groups = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Group[]>>('/api/groups', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
    post: (body: Partial<Group>) => this.post<Group>('/api/groups', body),
    get: (groupId: string) =>
      this.get<Group>(
        `/api/groups/${groupId}`,
        {},
        {
          headers: createHeaders(featureHeader(CoreFeature.SlimContainers)),
        },
      ),
    put: (groupId: string, body: Partial<Group>) => this.put<WriteResult>(`/api/groups/${groupId}`, body),
    delete: (groupId: string) => this.delete<WriteResult>(`/api/groups/${groupId}`),
    projects: (groupId: string, params?: ApiParams) =>
      this.get<Project[]>(`/api/groups/${groupId}/projects`, params, {
        headers: createHeaders(featureHeader(CoreFeature.SlimContainers)),
      }),
    permissions: {
      get: (groupId: string, userId: string) =>
        this.get<AccessPermission>(`/api/groups/${groupId}/permissions/${userId}`),
      post: (groupId: string, body: AccessPermission) =>
        this.post<AccessPermission>(`/api/groups/${groupId}/permissions`, body),
      put: (groupId: string, userId: string, body: AccessPermission) =>
        this.put<AccessPermission>(`/api/groups/${groupId}/permissions/${userId}`, body),
      delete: (groupId: string, userId: string) => this.delete<null>(`/api/groups/${groupId}/permissions/${userId}`),
      templates: {
        get: (groupId: string, userId: string) =>
          this.get<RolePermission>(`/api/groups/${groupId}/permissions/templates/${userId}`),
        post: (groupId: string, body: Partial<RolePermission>, params?: { propagate: boolean }) =>
          this.post<WriteResult>(`/api/groups/${groupId}/permissions/templates`, body, { params }),
        put: (groupId: string, userId: string, body: Partial<RolePermission>, params?: { propagate: boolean }) =>
          this.put<WriteResult>(`/api/groups/${groupId}/permissions/templates/${userId}`, body, { params }),
        delete: (groupId: string, userId: string, params?: { propagate: boolean }) =>
          this.delete<WriteResult>(`/api/groups/${groupId}/permissions/templates/${userId}`, { params }),
      },
    },
    roles: {
      query: (groupId: string, params: ApiParams = {}) => this.get<Role[]>(`/api/groups/${groupId}/roles`, params),
      get: (groupId: string, roleId: string) => this.get<Role>(`/api/groups/${groupId}/roles/${roleId}`),
      post: (groupId: string, body: Partial<Role>) => this.post<WriteResult>(`/api/groups/${groupId}/roles`, body),
      delete: (groupId: string, roleId: string) => this.delete<WriteResult>(`/api/groups/${groupId}/roles/${roleId}`),
    },
    tags: {
      post: (groupId: string, tagValue: string) =>
        this.post<WriteResult>(`/api/groups/${groupId}/tags`, { value: tagValue }),
      delete: (groupId: string, tagValue: string) =>
        this.delete<WriteResult>(`/api/groups/${groupId}/tags/${encodeURIComponent(tagValue)}`),
      put: (groupId: string, oldTagValue: string, newTagValue: string) =>
        this.put<string>(`/api/groups/${groupId}/tags/${encodeURIComponent(oldTagValue)}`, { value: newTagValue }),
    },
    settings: {
      get: (groupId: string) => this.get<GroupSettings>(`/api/groups/${groupId}/settings`),
      put: (groupId: string, body: Partial<GroupSettings>) =>
        this.put<WriteResult>(`/api/groups/${groupId}/settings`, body),
    },
  };

  readonly ioProxy = {
    dimse: {
      peers: {
        test: (host: string, port: number, aet: string) =>
          this.get<DimsePeersTest>(`/io-proxy/dimse/peers/test?host=${host}&port=${port}${aet ? `&aet=${aet}` : ''}`),
      },
    },
  };

  readonly jobs = {
    detail: (jobId: string) => this.get<JobDetail>(`/api/jobs/${jobId}/detail`),
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Job[]>>('/api/jobs', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (jobId: string) => this.get<Job>(`/api/jobs/${jobId}`),
    put: (jobId: string, body: Partial<Job>) => this.put<null>(`/api/jobs/${jobId}`, body),
    add: (body: AddJob) => this.post<Pick<Job, '_id'>>('/api/jobs/add', body),
    retry: (jobId: string) => this.post<Pick<Job, '_id'>>(`/api/jobs/${jobId}/retry`),
    batch: {
      create: (body: ClassicBatchProposalInput) => this.post<BatchJob>('/api/batch', body),
      run: (batchId: string) => this.post<Job[]>(`/api/batch/${batchId}/run`),
    },
    logs: {
      json: (jobId: string) => this.get<JobLogResponse>(`/api/jobs/${jobId}/logs`),
      html: (jobId: string) =>
        this.get<string>(`/api/jobs/${jobId}/logs/html`, undefined, {
          headers: {
            accept: 'text/html',
          },
          responseType: 'text',
        }),
      text: (jobId: string) =>
        this.get<string>(`/api/jobs/${jobId}/logs/text`, undefined, {
          headers: {
            accept: 'text/plain',
          },
          responseType: 'text',
        }),
    },
    bulk: {
      cancel: (jobs: string[]) => this.put<JobBulkActionResult>('/api/jobs/cancel/bulk', { jobs }),
      retry: (jobs: string[]) => this.put<JobBulkActionResult>('/api/jobs/retry/bulk', { jobs }),
      updatePriority: (jobs: string[], priority: JobPriority) =>
        this.put<JobPriorityUpdateActionResult>('/api/jobs/priority', { job_ids: jobs, priority: priority }),
    },
  };

  readonly jupyterlab_servers = {
    query: (params: ApiParams = {}) => this.get<Paginated<JupyterLabServer[]>>('/api/jupyterlab_servers', params),
    post: (body: JupyterLabServerInput) => this.post<JupyterLabServer>('/api/jupyterlab_servers', body),
    put: (jupyterlabServerId: string, body: Partial<JupyterLabServer>) =>
      this.put<WriteResult>(`/api/jupyterlab_servers/${jupyterlabServerId}`, body),
    delete: (jupyterlabServerId: string) => this.delete<WriteResult>(`/api/jupyterlab_servers/${jupyterlabServerId}`),
    ticket: (jupyterlabServerId: string, params: ApiParams = {}) =>
      this.get<DownloadTicket>(`/api/jupyterlab_servers/${jupyterlabServerId}/data/ticket`, params),
    download: (jupyterlabServerId: string, ticketId: string) =>
      this.get<Blob>(`/api/jupyterlab_servers/${jupyterlabServerId}/data?ticket_id=${ticketId}`),
    start: (jupyterlabServerId: string, configId: string) =>
      this.put<JupyterLabServer>(`/api/jupyterlab_servers/${jupyterlabServerId}/start`, {
        server_options_id: configId,
      }),
    stop: (jupyterlabServerId: string) =>
      this.put<JupyterLabServer>(`/api/jupyterlab_servers/${jupyterlabServerId}/stop`),
    serverOptions: (params?: ApiParams) =>
      this.get<JupyterLabServerOption[]>('/api/jupyterlab_servers/server_options', params),
  };

  readonly login = {
    basic: (payload: { email: string; password: string }) => this.post<{ token: string }>('/api/login/basic', payload),
    logout: () => this.post<void>('/api/logout'),
    restLogin: (payload: { auth_type: string; code: string }) => this.post<{ token: string }>('/api/login', payload),
  };

  readonly modalities = {
    get: () => this.get<Modality[]>('/api/modalities'),
  };

  readonly permissions = {
    check: {
      action: (action: PermissionAction, projectIds: string[]) =>
        this.put<PermissionCheckOutput>('/api/permissions/check', [{ action, project_ids: projectIds }]),
    },
    put: (body: PermissionCheckInput) => this.put<PermissionCheckOutput>('/api/permissions/check', body),
    self: () => this.get<ProjectPermissions>('/api/permissions/self'),
  };

  /**
   * Builds the endpoints for a v3 overlay resource. Annotations and segmentations share an identical API
   * shape, differing only in their path and record type.
   */
  private overlayEndpoints<T extends Overlay>(resource: 'annotations' | 'segmentations'): OverlayEndpoints<T> {
    const baseUrl = `/api/v3/${resource}`;
    return {
      get: (params: ApiParams) => this.get<T[]>(baseUrl, params),
      count: (countRequest: OverlayCountRequest) => this.post<OverlayCountResult>(`${baseUrl}/count`, countRequest),
      getFile: (id: string, params?: ApiParams, headers?: ApiHeaders) =>
        this.get<Blob>(`${baseUrl}/${id}/file`, { ...params, view: true }, { headers, responseType: 'blob' }),
      delete: (id: string, params?: ApiParams) => this.delete<DeleteResult>(`${baseUrl}/${id}`, { params }),
      deleteVersion: (id: string, version: number, params?: ApiParams) =>
        this.delete<DeleteResult>(`${baseUrl}/${id}/versions/${version.toString()}`, { params }),
      restoreVersion: (id: string, version: number) =>
        this.post<T>(`${baseUrl}/${id}/versions/${version.toString()}/restore`, null),
      getDeleteReasons: () => this.get<DeleteReasons>(`${baseUrl}/delete_reasons`),
    };
  }

  readonly annotations = this.overlayEndpoints<Annotation>('annotations');

  readonly segmentations = this.overlayEndpoints<Segmentation>('segmentations');

  readonly malwareTriage = {
    list: (params: ApiParams) => this.get<Paginated<MalwareFinding[]>>('/api/files/malware-triage', params),
    // An empty action resets the decision
    applyDecision: (findingId: string, action: TriageDecision | null) =>
      this.put<WriteResult>(`/api/files/malware-triage/${findingId}`, null, { params: { action: action ?? '' } }),
  };

  readonly projects = {
    query: (params?: ApiParams) =>
      this.get<Paginated<ListProject[]>>('/api/projects', params, {
        headers: createHeaders(
          featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers, CoreFeature.ExcludeFiles),
        ),
      }).pipe(
        // FLYW-9674 clearly distinguish that files are excluded and not empty
        map(
          (response) =>
            ({ ...response, results: response.results.map((result) => omit(result, 'files')) }) as Paginated<Project[]>,
        ),
      ),
    get: (projectId: string, params?: ApiParams) =>
      this.get<Project>(`/api/projects/${projectId}`, params, {
        headers: createHeaders(featureHeader(CoreFeature.SlimContainers, CoreFeature.ExcludeFiles)),
      }).pipe(
        // FLYW-9674 clearly distinguish that files are excluded and not empty
        map((result) => omit(result, 'files') as Project),
      ),
    post: (body: Partial<Project>, params?: { inherit: boolean }) =>
      this.post<Project>('/api/projects', body, { params }),
    put: (projectId: string, body: Partial<Project>) => this.put<WriteResult>(`/api/projects/${projectId}`, body),
    delete: (projectId: string, params?: ApiParams) =>
      this.delete<WriteResult>(`/api/projects/${projectId}`, { params }),
    jobs: (projectId: string, params: ApiParams = {}) =>
      this.get<Paginated<Job[]>>(`/api/projects/${projectId}/jobs`, params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    fileCounts: (projectId: string) => this.get<FileCounts>(`/api/projects/${projectId}/file-counts`),
    auditTrail: {
      get: (params?: ApiParams) =>
        this.get<Paginated<AuditTrail[]>>('/api/audit-trail/reports', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }),
      signedUrl: (reportId: string) => this.get<string>(`/api/audit-trail/reports/${reportId}/download`),
      post: (body: AuditTrailReportPost) => this.post<AuditTrail>('/api/audit-trail/reports', body),
      delete: (reportId: string) => this.delete<void>(`/api/audit-trail/reports/${reportId}`),
      download: (reportId: string) =>
        this.get<string>(
          `/api/audit-trail/reports/${reportId}/csv`,
          {},
          {
            headers: {
              accept: 'text/csv',
            },
            responseType: 'text',
          },
        ),
      cancel: (reportId: string) => this.patch<void>(`/api/audit-trail/reports/${reportId}`, { status: 'canceled' }),
    },
    catalogList: {
      get: (params?: ApiParams) =>
        this.get<Paginated<CatalogProject[]>>('/api/projects/catalog-list', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }),
    },
    catalogListFilterOptions: {
      get: () => this.get<ProjectSharingFilterOptions>('/api/projects/catalog-list-filter-options'),
    },
    copy: {
      get: (projectId: string) => this.get<string[]>(`/api/projects/${projectId}/copies`),
      post: (projectId: string, body: ProjectCopyInput) =>
        this.post<ProjectCopyOutput>(`/api/projects/${projectId}/copy`, body),
      status: (projectId: string, snapshotId: string) =>
        this.get<ProjectCopyStatus>(`/api/projects/${projectId}/copy/${snapshotId}/status`),
    },
    cvatExportFormats: {
      get: () => this.get<CvatExportFormats[]>('/api/cvat/export-formats'),
    },
    labels: {
      get: (params: ApiParams = {}) => this.get<ProjectLabel[]>('/api/projects/labels', params),
    },
    locking: {
      post: (body) =>
        this.post<WriteResult>(`/api/projects/${body.projectId}/lock`, body, {
          headers: createHeaders({ 'X-MFA-Code': body.code }),
        }),
    },
    unlocking: {
      post: (body) =>
        this.post<WriteResult>(`/api/projects/${body.projectId}/unlock`, null, {
          headers: createHeaders({ 'X-MFA-Code': body.code }),
        }),
    },
    rules: {
      query: (projectId: string, params?: ApiParams) =>
        this.get<GearRule[]>(`/api/projects/${projectId}/rules`, params),
      post: (projectId: string, body: GearRuleInput) => this.post<GearRule>(`/api/projects/${projectId}/rules`, body),
      put: (projectId: string, ruleId: string, body: GearRuleInput) =>
        this.put<GearRule>(`/api/projects/${projectId}/rules/${ruleId}`, body),
      delete: (projectId: string, ruleId) => this.delete<null>(`/api/projects/${projectId}/rules/${ruleId}`),
    },
    subjects: (projectId: string, params: ApiParams = {}) =>
      this.get<Paginated<Subject[]>>(`/api/projects/${projectId}/subjects`, params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
    sessions: (projectId: string, params: ApiParams = {}) =>
      this.get<Paginated<Session[]>>(`/api/projects/${projectId}/sessions`, params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
    settings: {
      get: (projectId: string, params?: ApiParams) =>
        this.get<ProjectSettings>(`/api/projects/${projectId}/settings`, params),
      put: (projectId: string, body: Partial<ProjectSettings>) =>
        this.put<ProjectSettings>(`/api/projects/${projectId}/settings`, body),
      deidProfile: {
        get: (projectId: string) => this.get<DeidProfile>(`/api/projects/${projectId}/settings/deid_profile`),
      },
    },
    permissions: {
      get: (projectId: string, userId: string) =>
        this.get<RolePermission>(`/api/projects/${projectId}/permissions/${userId}`),
      post: (projectId: string, body: RolePermission) =>
        this.post<RolePermission>(`/api/projects/${projectId}/permissions`, body),
      put: (projectId: string, userId: string, body: RolePermission) =>
        this.put<RolePermission>(`/api/projects/${projectId}/permissions/${userId}`, body),
      delete: (projectId: string, userId: string) =>
        this.delete<null>(`/api/projects/${projectId}/permissions/${userId}`),
    },
    packfile: {
      start: (projectId: string) => this.post<{ token: string }>(`/api/projects/${projectId}/packfile-start`),
      pack: (projectId: string, body: any, params: { token: string }) =>
        this.post<File>(`/api/projects/${projectId}/packfile`, body, { params }),
      end: (projectId: string, params: { token: string; metadata: string }) =>
        this.get(`/api/projects/${projectId}/packfile-end`, params),
    },
    templates: {
      set: (projectId: string, templates: ProjectTemplate[]) =>
        this.post<{ modified: number; jobs_spawned: number }>(`/api/projects/${projectId}/template`, { templates }),
    },
  };

  // deprecated
  readonly readerTasks = {
    query: (params?: ApiParams) => this.get<Paginated<DeprecatedTask[]>>('/api/readertasks', params),
    post: (body: DeprecatedTaskInput) => this.post<DeprecatedTask>('/api/readertasks', body),
    put: (taskId: string, body: DeprecatedTaskInput) => this.put<DeprecatedTask>(`/api/readertasks/${taskId}`, body),
    get: (taskId: string) => this.get<DeprecatedTask>(`/api/readertasks/${taskId}`),
    details: (taskId: string) => this.get<DeprecatedTaskDetails>(`/api/readertasks/${taskId}/details`),
    delete: (taskId: string) => this.delete<WriteResult>(`/api/readertasks/${taskId}`),
    project: {
      get: (projectId: string, params?: ApiParams) =>
        this.get<DeprecatedTask[]>(`/api/readertasks/project/${projectId}`, params),
    },
    batch: {
      create: (body: DeprecatedBulkTaskInput) => this.post<DeprecatedTask[]>('/api/readertasks/batch', body),
      dryrun: (body: DeprecatedBulkTaskInput) =>
        this.post<DeprecatedBulkTaskDryrunResult>('/api/readertasks/batch/dryrun', body),
      duplicates: (body: DeprecatedBulkTaskInput) =>
        this.post<Paginated<DeprecatedTask[]>>('/api/readertasks/batch/duplicates', body),
      parents: (body: DeprecatedTaskParentsEstimateFilter) =>
        this.post<DeprecatedMatchingContainerCount>('/api/readertasks/batch/parents', body),
    },
  };

  readonly readTaskProtocols = {
    query: (params: ApiParams = {}, includeDeleted = false) =>
      this.get<Paginated<ReadTaskProtocol[]>>('/api/read_task_protocols', {
        ...params,
        include_deleted: includeDeleted,
      }),
    get: (protocolId: string) => this.get<ReadTaskProtocol>(`/api/read_task_protocols/${protocolId}`),
    post: (body: ReadTaskProtocolInput) => this.post<ReadTaskProtocol>('/api/read_task_protocols', body),
    put: (protocolId: string, body: ReadTaskProtocolInput) =>
      this.put<ReadTaskProtocol>(`/api/read_task_protocols/${protocolId}`, body),
    delete: (protocolId: string) => this.delete<WriteResult>(`/api/read_task_protocols/${protocolId}`),
    deleteDryrun: (protocolId: string, params?: ApiParams) =>
      this.get<Paginated<DeprecatedDryrunTaskDeletionPreview[]>>(
        `/api/read_task_protocols/${protocolId}/dry_run_delete`,
        {
          ...params,
          exhaustive: true,
        },
      ),
  };

  readonly report = {
    accessLog: {
      query: (params?: ApiParams) =>
        this.get<AccessLog[]>('/api/report/accesslog', params, {
          // headers: createHeaders(featureHeader(CoreFeature.Pagination)), // blocked by FLYW-16590
        }),
      getAccessTypes: (params?: ApiParams) => this.get<string[]>('/api/report/accesslog/types', params, {}),
      csv: (params?: ApiParams) =>
        this.get<string>('/api/report/accesslog', params, {
          responseType: 'text',
        }),
    },
    project: {
      getProjectReport: (options?: ProjectReportOptions) =>
        this.get<ProjectReportsResponse>('/api/report/project', options),
    },
    usage: {
      getDashboard: () => this.get<UsageDashboard>('/api/report/usage-summary/dashboard'),
      getReportPaginated: (options?: UsageReportOptions) =>
        this.get<Paginated<UsageReportLineItem[]>>('/api/report/usage-summary', options),
      getReport: (options?: UsageReportOptions) =>
        this.get<UsageReportLineItem[]>('/api/report/usage-summary', options),
      csv: (options?: UsageReportCsvOptions) =>
        this.get<string>('/api/report/usage-summary', options, {
          headers: {
            accept: 'text/csv',
          },
          responseType: 'text',
        }),
    },
    queue: {
      query: (params: ApiParams = {}) =>
        this.get<Paginated<TaskReport[]>>('/api/reports', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }),
      get: (reportType: string, params?: ApiParams) => this.get<TaskReport>(`/api/${reportType}/reports`, params),
      post: (params: ApiParams, reportType: string) => this.post<TaskReport>(`/api/${reportType}/reports`, params),
      delete: (reportType: string, reportId: string) =>
        this.delete<WriteResult>(`/api/${reportType}/reports/${reportId}`),
      download: (reportType: string, reportId: string) =>
        this.get<string>(
          `/api/${reportType}/reports/${reportId}/csv`,
          {},
          {
            headers: {
              accept: 'text/csv',
            },
            responseType: 'text',
          },
        ),
    },
  };

  readonly resolve = {
    resolve: (body: { path: string[] }, params?: ApiParams) => this.post<Resolve>('/api/resolve', body, params),
  };

  readonly roles = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Role[]>>('/api/roles', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)), // NOTE: not paginated on old core
      }),
    get: (roleId: string) => this.get<Role>(`/api/roles/${roleId}`),
    post: (body: Partial<Role>) => this.post<Role>('/api/roles', body),
    put: (roleId: string, body: Partial<Role>) => this.put<WriteResult>(`/api/roles/${roleId}`, body),
    delete: (roleId: string) => this.delete<WriteResult>(`/api/roles/${roleId}`),
  };

  readonly sessions = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Session[]>>('/api/sessions', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
    get: (sessionId: string, params?: ApiParams) =>
      this.get<Session>(`/api/sessions/${sessionId}`, params, {
        headers: createHeaders(featureHeader(CoreFeature.SlimContainers)),
      }),
    post: (body: SessionPostPayload) => this.post<{ _id: string }>('/api/sessions', body),
    put: (sessionId: string, body: Partial<Session>) => this.put<WriteResult>(`/api/sessions/${sessionId}`, body),
    delete: (sessionId: string, params?: ApiParams) =>
      this.delete<WriteResult>(`/api/sessions/${sessionId}`, { params }),
    deleteBulk: (sessionIds: string[], params?: ApiParams) =>
      this.delete<WriteResult>('/api/sessions', { params, body: sessionIds }),
    deleteQuery: (query: SearchQuery, params?: ApiParams) =>
      this.delete<WriteResult>('/api/sessions/delete_by_query', { params, body: { query } }),
    jobs: (sessionId: string, params: ApiParams = {}) =>
      this.get<{ jobs: Job[] }>(`/api/sessions/${sessionId}/jobs`, params),
    acquisitions: (sessionId: string, params?: ApiParams) =>
      this.get<Paginated<Acquisition[]>>(`/api/sessions/${sessionId}/acquisitions`, params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
  };

  readonly site = {
    providers: {
      query: (params?: ApiParams) =>
        this.get<Paginated<Provider[]>>('/api/site/providers', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }),
      get: (providerId: string) => this.get<Provider>(`/api/site/providers/${providerId}`),
    },
    rules: {
      query: (params?: ApiParams) => this.get<GearRule[]>('/api/site/rules', params),
      post: (body: GearRuleInput) => this.post<GearRule>('/api/site/rules', body),
      put: (ruleId: string, body: GearRuleInput) => this.put<GearRule>(`/api/site/rules/${ruleId}`, body),
      delete: (ruleId) => this.delete<null>(`/api/site/rules/${ruleId}`),
    },
    settings: {
      get: () => this.get<SiteSettings>('/api/site/settings'),
      put: (body: Partial<SiteSettings>) => this.put<SiteSettings>('/api/site/settings', body),
    },
    bookmarks: {
      get: () => this.get<SiteBookmark[]>('/api/site/bookmark-list'),
      put: (bookmarks) => this.put<SiteBookmark[]>('/api/site/bookmark-list', bookmarks),
    },
  };

  readonly snapshot = {
    projects: {
      snapshots: {
        query: (projectId: string, params?: ApiParams) =>
          this.get<Paginated<Snapshot[]>>(`/snapshot/projects/${projectId}/snapshots`, params, {
            headers: createHeaders(featureHeader(CoreFeature.Pagination)),
          }),
        detail: (projectId: string, snapshotId: string, params?: ApiParams) =>
          this.get<Snapshot>(`/snapshot/projects/${projectId}/snapshots/${snapshotId}/detail`, params),
      },
    },
  };

  readonly staffingPools = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<StaffingPool[]>>('/api/staffing_pools', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (staffingPoolId: string) => this.get<StaffingPool>(`/api/staffing_pools/${staffingPoolId}`),
    post: (body: StaffingPoolPayload) => this.post<StaffingPool>('/api/staffing_pools', body),
    put: (staffingPoolId: string, body: Partial<StaffingPoolPayload>) =>
      this.put<StaffingPool>(`/api/staffing_pools/${staffingPoolId}`, body),
    delete: (staffingPoolId: string) => this.delete<WriteResult>(`/api/staffing_pools/${staffingPoolId}`),
    users: {
      put: (userId: string, body: UserStaffingPoolsPayload) =>
        this.put<WriteResult>(`/api/staffing_pools/users/${userId}/staffing_pools`, body),
    },
  };

  readonly subjects = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Subject[]>>('/api/subjects', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination, CoreFeature.SlimContainers)),
      }),
    get: (subjectId: string, params: ApiParams = {}) =>
      this.get<Subject>(`/api/subjects/${subjectId}`, params, {
        headers: createHeaders(featureHeader(CoreFeature.SlimContainers)),
      }),
    put: (subjectId: string, body: Partial<Subject>) => this.put<WriteResult>(`/api/subjects/${subjectId}`, body),
    post: (body: Partial<Subject>) => this.post<Pick<Subject, '_id'>>('/api/subjects', body),
    delete: (subjectId: string) => this.delete<WriteResult>(`/api/subjects/${subjectId}`),
  };

  readonly tasks = {
    reader: {
      get: (taskId: string) => this.get<ReaderTask>(`/api/tasks/reader/${taskId}`),
      post: (body: ReaderTaskCreate) => this.post<ReaderTask>('/api/tasks/reader', body),
      put: (taskId: string, body: ReaderTaskModify) => this.put<ReaderTask>(`/api/tasks/reader/${taskId}`, body),
      query: (params?: ApiParams) => this.get<Paginated<ReaderTask[]>>('/api/tasks/reader', params),
      /**
       * Same query, but supports HTTP conditional requests: pass the `ETag` from a previous
       * response as `ifNoneMatch` to let the server answer `304 Not Modified` (surfaced by
       * Angular as an `HttpErrorResponse` with status 304, since only the 2xx range is "ok")
       * when the list hasn't changed. Full response is observed so the caller can read the
       * `ETag` response header, which isn't exposed on `Observable<T>` body-only responses.
       */
      queryConditional: (params?: ApiParams, ifNoneMatch?: string) =>
        this.http.get<Paginated<ReaderTask[]>>('/api/tasks/reader', {
          params: createParams(params),
          headers: ifNoneMatch ? createHeaders({ 'If-None-Match': ifNoneMatch }) : undefined,
          observe: 'response',
        }),
      batch: (body: BatchReaderTaskCreate) => this.post<ReaderTask[]>('/api/tasks/reader/batch', body),
      facets: <K extends string>(facetField: TaskFacet, params: ApiParams = {}) =>
        this.get<Partial<Record<K, number>>>(`/api/tasks/reader/facets/${facetField}`, params),
      assign: (taskId: string, body: TaskAssign) => this.put<ReaderTask>(`/api/tasks/reader/${taskId}/assign`, body),
      complete: (taskId: string, body: { mfa_code?: string; read_number?: number; reason?: string } = {}) =>
        this.post<ReaderTask>(`/api/tasks/reader/${taskId}/complete`, body),
      clone: (taskId: string) => this.post<ReaderTask>(`/api/tasks/reader/${taskId}/clone`, {}),
      adjudication: (body: AdjudicationTaskCreate) => this.post<ReaderTask[]>('/api/tasks/reader/adjudication', body),
      adjudicationBatch: (body: BatchAdjudicationTaskCreate) =>
        this.post<ReaderTask[]>('/api/tasks/reader/adjudication/batch', body),
    },
    upload: {
      query: (params?: ApiParams) => this.get<Paginated<UploadTask[]>>('/api/tasks/upload', params),
      get: (taskId: string) => this.get<UploadTask>(`/api/tasks/upload/${taskId}`),
    },
    container: {
      get: (containerType: ContainerType, ids?: string[]) =>
        this.get<string[]>(`/api/tasks/${pluralize(containerType)}/ids`, { ids }),
    },
  };

  readonly responses = {
    get: (responseId: string) => this.get<FormResponseOutput>(`/api/responses/${responseId}`),
    patch: (responseId: string, body: FormResponseBase) =>
      this.patch<FormResponseOutput>(`/api/responses/${responseId}`, body),
    submit: (responseId: string) => this.post<FormResponseOutput>(`/api/responses/${responseId}/submit`, {}),
    tasks: {
      query: (params: ApiParams & { taskId: string }) =>
        this.get<Paginated<FormResponseOutput[]>>(`/api/tasks/${params.taskId}/responses`),
      post: (taskId: string, body: FormResponseCreate) =>
        this.post<FormResponseOutput>(`/api/tasks/${taskId}/responses`, body),
      put: (taskId: string, responseId: string, body: FormResponseCreate) =>
        this.put<FormResponseOutput>(`/api/tasks/${taskId}/responses/${responseId}`, body),
    },
  };

  readonly tree = {
    query: <RootType>(body: TreeQuery, params: ApiParams = {}) =>
      this.post<Paginated<(RootType & TreeResult)[]>>('/api/tree', body, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        params: params,
      }),
  };

  readonly users = {
    apiKeys: {
      delete: (id: string) => this.delete<null>(`/api/users/self/key/${id}`),
      generateKey: (params?: ApiKeyParams) => this.post<ApiKeyResult>('/api/users/self/key', params),
    },
    query: (params: ApiParams = {}) =>
      this.get<Paginated<User[]>>('/api/users', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (userId: string) => this.get<User>(`/api/users/${userId}`),
    put: (userId: string, body: Partial<User>, params?: ApiParams) =>
      this.put<WriteResult>(`/api/users/${userId}`, body, { params }),
    post: (body: Partial<User>) => this.post<User>('/api/users', body),
    delete: (userId: string) => this.delete<WriteResult>(`/api/users/${userId}`),
    mfaSettings: {
      get: () => this.get<MultifactorSettings>('/api/users/self/mfa-setups'),
      post: (body) => this.post<MultifactorPrompt>('/api/users/self/mfa-setups', body),
      patch: (body) =>
        this.patch<MultifactorPrompt>('/api/users/self/mfa-setups', body, {
          headers: createHeaders({ 'X-MFA-Code': body.code }),
        }),
      delete: (body) =>
        this.delete<MultifactorPrompt>('/api/users/self/mfa-setups', {
          headers: createHeaders({ 'X-MFA-Code': body.code }),
        }),
    },
    mfaVerifications: {
      post: () => this.post<null>('/api/users/self/mfa-verifications'),
    },
    projectsForUser: (userId: string) => this.get<Project[]>(`/api/users/${userId}/projects`),
    self: () => this.get<Profile>('/api/users/self'),
    sendMfaCode: (body?: { channel?: string }) =>
      this.post<{ channel: string; last_two_digits: string }>('/api/users/self/mfa/code', body),
    updateVisitedProjects: (projectId: string) => this.post<string[]>(`/api/users/self/visited_projects/${projectId}`),
    updatePreferences: (body: UserPreferences) => this.put<User>('/api/users/self/preferences', body),
  };

  readonly version = (): Observable<Version> => this.get<Version>('/api/version');

  readonly views = {
    query: (containerId: string, params: ApiParams = {}) =>
      this.get<Paginated<DataView[]>>(`/api/containers/${containerId}/views`, params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (viewId: string) => this.get<DataView>(`/api/views/${viewId}`),
    preview: (containerId: string, body: Pick<DataViewPayload, 'columns'>) =>
      this.post<DataViewResponse>(`/api/views/data?containerId=${containerId}`, body),
    create: (containerId: string, body: DataViewPayload) =>
      this.post<DataViewsCreateResponse>(`/api/containers/${containerId}/views`, body),
    promote: (body: DataViewPayload) => this.post<DataViewsCreateResponse>('/api/containers/site/views', body),
    update: (viewId: string, body: DataViewPayload) => this.put<any>(`/api/views/${viewId}`, body),
    delete: (viewId: string) => this.delete<any>(`/api/views/${viewId}`),
    data: {
      get: (viewId: string, params: ApiParams = {}) => this.get<DataViewResponse>(`/api/views/${viewId}/data`, params),
      export: (viewId: string, params: ApiParams = {}) =>
        this.get<any>(`/api/views/${viewId}/data`, params, {
          headers: {
            accept: String(params.format).includes('json') ? 'application/json' : `text/${params.format}`,
          },
          responseType: 'text',
        }),
      ticket: (viewId: string, params: ApiParams = {}) =>
        this.get<DownloadTicket>(`/api/views/${viewId}/data`, { ...params, ticket: '' }),
    },
    columns: {
      query: (params: ApiParams = {}) => this.get<ViewColumn[]>('/api/views/columns', params),
    },
    queue: {
      adhoc: (body: DataView) => this.post<DataViewExecution>('/api/views/queue', body),
      saved: (viewId: string, containerId: string) =>
        this.post<DataViewExecution>(`/api/views/${viewId}/queue?containerId=${containerId}`),
    },
  };

  readonly viewerConfigs = {
    get: (configId: string) => this.get<ViewerConfig>(`/api/viewerconfigs/${configId}`),
    query: (params: ApiParams = {}) => this.get<Paginated<ViewerConfig[]>>('/api/viewerconfigs', params),
    post: (body: ViewerConfigInput) => this.post<ViewerConfig>('/api/viewerconfigs', body),
    put: (configId: string, body: ViewerConfigInput) => this.put<ViewerConfig>(`/api/viewerconfigs/${configId}`, body),
    delete: (configId: string) => this.delete<WriteResult>(`/api/viewerconfigs/${configId}`),
  };

  readonly xfer = {
    connectors: {
      query: (params: ApiParams = {}) => this.get<XferConnectorResponse>('/xfer/connectors', params),
    },
    storages: {
      query: (params?: ApiParams) =>
        this.get<Paginated<XferStorage[]>>('/xfer/storages', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }),
      get: (storageId: string, params?: ApiParams) => this.get<XferStorage>(`/xfer/storages/${storageId}`, params),
      post: (body: Partial<XferStorage>) => this.post<XferStorage>('/xfer/storages', body),
      patch: (storageId: string, body: Partial<XferStorage>) =>
        this.patch<XferStorage>(`/xfer/storages/${storageId}`, body),
      delete: (storageId: string) => this.delete<void>(`/xfer/storages/${storageId}`),
      check: (storageId: string) => this.post<StorageCheckResponse>(`/xfer/storages/${storageId}/check`),
    },
    exports: {
      query: (params?: ApiParams) =>
        this.get<Paginated<XferExport[]>>('/xfer/exports', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }).pipe(map((exportPage) => ({ ...exportPage, results: exportPage.results }))),
      get: (exportId: string, params?: ApiParams) => this.get<XferExport>(`/xfer/exports/${exportId}`, params),
      post: (body: Export) => this.post<XferExport>('/xfer/exports', body),
      report: (exportId: string, params?: ApiParams) => this.get<XferUrl>(`/xfer/exports/${exportId}/report`, params),
      cancelExport: (exportId: string) => this.post<XferExport>(`/xfer/exports/${exportId}/cancel`),
      progress: (exportId: string) => this.get<ExportProgress2>(`/xfer/exports/${exportId}/progress2`),
    },
    imports: {
      query: (params?: ApiParams) =>
        this.get<Paginated<XferImport[]>>('/xfer/imports', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }).pipe(map((page) => ({ ...page, results: page.results }))),
      get: (importId: string, params?: ApiParams) => this.get<XferImport>(`/xfer/imports/${importId}`, params),
      cancel: (importId: string) => this.post<XferImport>(`/xfer/imports/${importId}/cancel`),
      rerun: (importId: string) => this.post<XferImport>(`/xfer/imports/${importId}/rerun`),
      conflictReport: (params?: ApiParams) => this.get<XferUrl>('/xfer/conflicts/report', params),
      report: (importId: string, params?: ApiParams) => this.get<XferUrl>(`/xfer/imports/${importId}/report`, params),
      progress: (importId: string) => this.get<XferImportProgress>(`/xfer/imports/${importId}/progress2`),
      run: (body: XferImportCreate) => this.post<XferImport>('/xfer/imports', body),
    },
    schedules: {
      get: (scheduleId: string, params?: ApiParams) => this.get<XferSchedule>(`/xfer/schedules/${scheduleId}`, params),
    },
    conflicts: {
      get: (params?: ApiParams) => this.get<Paginated<XferConflict[]>>('/xfer/conflicts', params),
      getResolveMethods: () => this.get<XferConflictTypeResolveMethods>('/xfer/conflicts/resolve-methods'),
      resolve: (body: XferConflictResolveInput) => this.post('/xfer/conflicts/resolve', body),
    },
    blobs: {
      create: (body: XferBlobCreate, params?: ApiParams) => this.post<XferPendingBlob>('/xfer/blobs', body, { params }),
      createSession: (body: XferBlobSessionCreate, params?: ApiParams) =>
        this.post<XferBlobSession>('/xfer/blob-sessions', body, { params }),
      upload: (blobId: string, params?: ApiParams) =>
        this.post<XferBlobUploadResponse>(`/xfer/blobs/${blobId}/upload`, null, { params }),
      finish: (blobId: string, body: XferBlobFinish) => this.post<XferBlob>(`/xfer/blobs/${blobId}/finish`, body),
      delete: (blobId: string) => this.delete<void>(`/xfer/blobs/${blobId}`),
      finishSession: (blobSessionId: string, params?: ApiParams) =>
        this.post<XferBlobSession>(`/xfer/blob-sessions/${blobSessionId}/finish`, null, { params }),
      deleteSession: (blobSessionId: string) => this.delete<void>(`/xfer/blob-sessions/${blobSessionId}`),
    },
    ruleSets: {
      query: (params?: ApiParams) =>
        this.get<Paginated<Omit<XferRuleSets, 'yaml'>[]>>('/xfer/rule-sets', params, {
          headers: createHeaders(featureHeader(CoreFeature.Pagination)),
        }),
      get: (ruleId: string, params?: ApiParams) => this.get<XferRuleSets>(`/xfer/rule-sets/${ruleId}`, params),
      create: (body: XferRuleCreate, params?: ApiParams) => this.post<XferRule>('/xfer/rule-sets', body, { params }),
      update: (id: string, body: Partial<XferRuleSets>, params?: ApiParams) =>
        this.patch<XferRuleSets>(`/xfer/rule-sets/${id}`, body, { params }),
      archive: (id: string, params?: ApiParams) =>
        this.post<{ message: string }>(`/xfer/rule-sets/${id}/archive`, { params }),
      restore: (id: string, params?: ApiParams) =>
        this.post<{ message: string }>(`/xfer/rule-sets/${id}/restore`, { params }),
      schema: (params?: ApiParams) => this.get<XferRuleSchema>('/xfer/rule-sets/schema', params),
    },
    dicomPeers: {
      query: (params: ApiParams = {}) => this.get<any>('/xfer/dicom-peers', params),
    },
    storeSCPS: {
      query: (params: ApiParams = {}) => this.get<Paginated<XferStoreSCPS[]>>('/xfer/store-scps', params),
      update: (id: string, body: Partial<XferStoreSCPS>, params?: ApiParams) =>
        this.patch<XferStoreSCPS>(`/xfer/store-scps/${id}`, body, { params }),
      addTrustedCas: (id: string, body: { cert: XferStoreSCPsPemCertificate }, params: ApiParams = {}) =>
        this.post<XferStoreSCPS>(`/xfer/store-scps/${id}/trusted-cas`, body, params),
      deleteTrustedCas: (scp_id: string, ca_id: string, params: ApiParams = {}) =>
        this.delete<{ _id: string; cert: XferStoreSCU }[]>(`/xfer/store-scps/${scp_id}/trusted-cas/${ca_id}`, params),
      getRuleSet: (id: string) => this.get<{ yaml: string }>(`/xfer/store-scps/${id}/rule-set`),
      putRuleSet: (id: string, body: { yaml: string }) =>
        this.put<{ yaml: string }>(`/xfer/store-scps/${id}/rule-set`, body),
    },
    storeSCUS: {
      query: (params: ApiParams = {}) => this.get<Paginated<XferStoreSCU[]>>('/xfer/store-scus', params),
      create: (body: Partial<XferStoreSCU>) => this.post<XferStoreSCU>('/xfer/store-scus', body),
      update: (id: string, body: Partial<XferStoreSCU>, params?: ApiParams) =>
        this.patch<XferStoreSCU>(`/xfer/store-scus/${id}`, body, { params }),
      delete: (id: string) => this.delete<void>(`/xfer/store-scus/${id}`),
    },
    version: (params?: ApiParams) => this.get<XferVersion>('/xfer/version', params),
  };

  readonly customFilters = {
    query: <FilterType>(params: ApiParams = {}) => this.get<Paginated<FilterType[]>>('/api/custom_filters', params),
    post: <FilterInputType, FilterType>(body: FilterInputType) => this.post<FilterType>('/api/custom_filters', body),
    put: <FilterInputType, FilterType>(filterId: string, body: FilterInputType) =>
      this.put<FilterType>(`/api/custom_filters/${filterId}`, body),
    delete: (filterId: string) => this.delete<WriteResult>(`/api/custom_filters/${filterId}`),
  };

  readonly customForms = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<CustomForm[]>>('/api/customforms', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (formId: string) => this.get<CustomForm>(`/api/customforms/${formId}`),
    post: (body: CustomFormInput) => this.post<CustomForm>('/api/customforms', body),
    put: (formId: string, body: CustomFormModify) => this.put<CustomForm>(`/api/customforms/${formId}`, body),
    publish: (formId: string) => this.put<CustomForm>(`/api/customforms/${formId}/publish`),
    archive: (formId: string) => this.put<CustomForm>(`/api/customforms/${formId}/archive`),
    restore: (formId: string) => this.put<CustomForm>(`/api/customforms/${formId}/restore`),
    delete: (formId: string) => this.delete<WriteResult>(`/api/customforms/${formId}`),
  };

  readonly protocols = {
    query: (params: ApiParams = {}) =>
      this.get<Paginated<Protocol[]>>('/api/protocols', params, {
        headers: createHeaders(featureHeader(CoreFeature.Pagination)),
      }),
    get: (protocolId: string) => this.get<Protocol>(`/api/protocols/${protocolId}`),
    post: (body: ProtocolInput) => this.post<Protocol>('/api/protocols', body),
    put: (protocolId: string, body: ProtocolModify) => this.put<Protocol>(`/api/protocols/${protocolId}`, body),
    publish: (protocolId: string) => this.put<Protocol>(`/api/protocols/${protocolId}/publish`),
    archive: (protocolId: string) => this.put<Protocol>(`/api/protocols/${protocolId}/archive`),
    delete: (protocolId: string) => this.delete<WriteResult>(`/api/protocols/${protocolId}`),
  };

  get<T>(url: string, params?: ApiParams, options?: object): Observable<T> {
    return this.http.get<T>(url, { ...options, params: createParams(params) });
  }

  put<T>(url: string, body?: any, options?: object): Observable<T> {
    return this.http.put<T>(url, body, options);
  }

  patch<T>(url: string, body?: any, options?: object): Observable<T> {
    return this.http.patch<T>(url, body, options);
  }

  post<T>(url: string, body?: any, options?: object): Observable<T> {
    return this.http.post<T>(url, body, options);
  }

  delete<T>(url: string, options?: object): Observable<T> {
    return this.http.delete<T>(url, options);
  }
}

export function createParams(apiParams: ApiParams = {}): HttpParams {
  return Object.entries(apiParams).reduce((params, [key, value]) => {
    for (const val of Array.isArray(value) ? value : [value]) {
      params = params.append(key, String(val));
    }
    return params;
  }, new HttpParams());
}

export function createHeaders(headers: ApiHeaders): HttpHeaders {
  return new HttpHeaders(headers);
}

/**
 * Request a page of records, then request the rest if there are more.
 */
export function queryAll<T>(
  request: (params?: ApiParams) => Observable<Paginated<T[]>>,
  params: ApiParams = {},
): Observable<T[]> {
  return request(params).pipe(
    map(toPaginatedResponse),
    switchMap((response) =>
      response.total > response.count ? request({ ...params, limit: response.total }) : of(response),
    ),
    map((response) => response.results),
  );
}

/**
 * Convert plain array to paginated shape, or pass through already-paginated response.
 */
export function toPaginatedResponse<T>(response: T[] | Paginated<T[]>): Paginated<T[]> {
  if (Array.isArray(response)) {
    return { results: response, count: response.length, total: response.length };
  }
  return response;
}
